// src/components/pages/EventManager/EventAdminPage.js

import React, { useState, useEffect, useCallback } from "react";
import { db, auth } from "../../../firebase";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useTheme } from "styled-components";

// MUI
import {
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Organisms
import HeaderWithButton from "./organisms/HeaderWithButton";
import EventForm from "./organisms/EventForm";
import EventsTable from "./organisms/EventsTable";

const COLLECTION = "events";

const EventAdminPage = () => {
  const [isAdmin, setIsAdmin] = useState(false);

  // Create/Edit Modal
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // 'create' | 'edit'
  const [editingEvent, setEditingEvent] = useState(null); // full event object when editing

  // Delete Dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, title }

  // Snackbars
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useTheme();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (usr) => {
      if (usr) {
        const userDocRef = doc(db, "users", usr.uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setIsAdmin(!!data.admin);
        } else {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const openCreateModal = useCallback(() => {
    setFormMode("create");
    setEditingEvent(null);
    setFormOpen(true);
  }, []);

  const openEditModal = useCallback((evt) => {
    setFormMode("edit");
    setEditingEvent(evt || null);
    setFormOpen(true);
  }, []);

  const closeFormModal = useCallback(() => setFormOpen(false), []);

  const handleFormSuccess = useCallback(
    (msg = "Saved") => {
      setFormOpen(false);
      setSnackbar({ open: true, message: msg, severity: "success" });
    },
    []
  );

  const handleCloseSnackbar = useCallback(
    () => setSnackbar((s) => ({ ...s, open: false })),
    []
  );

  // Delete handlers
  const handleRequestDelete = useCallback((id, title) => {
    if (!id) return;
    setPendingDelete({ id, title: title || "this event" });
    setConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (deleting) return;
    setConfirmOpen(false);
    setPendingDelete(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete?.id) return;

    if (!isAdmin) {
      setSnackbar({
        open: true,
        message: "Not authorized to delete events.",
        severity: "error",
      });
      return;
    }

    setDeleting(true);
    try {
      await deleteDoc(doc(db, COLLECTION, pendingDelete.id));
      setSnackbar({
        open: true,
        message: `Deleted: ${pendingDelete.title}`,
        severity: "success",
      });
      setConfirmOpen(false);
      setPendingDelete(null);
    } catch (err) {
      console.error("[EventAdminPage] delete error:", err);
      setSnackbar({
        open: true,
        message:
          "Failed to delete the event. Please try again or check your permissions.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, isAdmin]);

  if (!isAdmin) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          Not authorized
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, fontFamily: "basic-sans" }}>
      {/* Header */}
      <HeaderWithButton
        title="Event Admin"
        buttonText="+ Create a new event"
        onButtonClick={openCreateModal}
        theme={theme}
      />

      {/* Events Table */}
      <EventsTable
        theme={theme}
        onRequestDelete={handleRequestDelete}
        onRequestEdit={openEditModal}
      />

      {/* Create/Edit Modal with form-style theming */}
      <Dialog
        open={formOpen}
        onClose={closeFormModal}
        fullWidth
        maxWidth="sm"
        aria-labelledby="event-form-title"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
            fontFamily: "basic-sans",
            border: `1px solid ${theme.colors.palePurple}`,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          id="event-form-title"
          sx={{
            bgcolor: theme.colors.headerPurple,
            color: theme.colors.amber,
            fontWeight: 700,
            borderBottom: `1px solid ${theme.colors.palePurple}`,
            pr: 6, // room for close icon
          }}
        >
          {formMode === "create" ? "Create Event" : "Edit Event"}
          <IconButton
            aria-label="close"
            onClick={closeFormModal}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: theme.colors.mediumPurple,
            borderColor: theme.colors.palePurple,
            p: 2,
          }}
        >
          <EventForm
            theme={theme}
            mode={formMode}
            initialEvent={editingEvent}
            showCard={false} // avoid double framing; dialog supplies the frame
            onSuccessfulSubmit={() =>
              handleFormSuccess(
                formMode === "create" ? "Event created" : "Event updated"
              )
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: theme.colors.mediumPurple,
            borderTop: `1px solid ${theme.colors.palePurple}`,
            p: 1.5,
          }}
        >
          <Button
            onClick={closeFormModal}
            sx={{
              color: theme.colors.amber,
              fontFamily: "basic-sans",
              "&:hover": { color: theme.colors.green, background: "transparent" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog (styled to match) */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="confirm-delete-title"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
            fontFamily: "basic-sans",
            border: `1px solid ${theme.colors.palePurple}`,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          id="confirm-delete-title"
          sx={{
            bgcolor: theme.colors.headerPurple,
            color: theme.colors.amber,
            fontWeight: 700,
            borderBottom: `1px solid ${theme.colors.palePurple}`,
            pr: 6,
          }}
        >
          Delete event?
          <IconButton
            aria-label="close"
            onClick={handleCancelDelete}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ bgcolor: theme.colors.mediumPurple, borderColor: theme.colors.palePurple }}
        >
          <Stack spacing={1}>
            <Typography variant="body1" sx={{ color: "white" }}>
              You are about to permanently delete:
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.colors.amber }}>
              {pendingDelete?.title || "this event"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
              This action cannot be undone.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: theme.colors.mediumPurple,
            borderTop: `1px solid ${theme.colors.palePurple}`,
            p: 1.5,
          }}
        >
          <Button
            onClick={handleCancelDelete}
            disabled={deleting}
            sx={{
              color: theme.colors.amber,
              fontFamily: "basic-sans",
              "&:hover": { color: theme.colors.green, background: "transparent" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            sx={{
              textTransform: "none",
              fontFamily: "basic-sans",
              borderRadius: "5px",
            }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={3}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EventAdminPage;
